import React, { ComponentPropsWithoutRef, PropsWithChildren, ReactElement, ReactNode, useMemo } from 'react';
import classnames from 'classnames';
import PriceComparison from '../../menu/PriceComparison';
import { useTranslation } from 'react-i18next';
import useProductLikes from '../../../hooks/global/useProductLikes';
import SeoResource from '../../../services/resources/SeoResource';
import { Heart, Plus } from 'lucide-react';
import HeartIcon from '../../../assets/logo/HeartIcon';
import RestrictedTooltip from '../restricted/RestrictedTooltip';
import { RESTRICTED_BG_CLASS, RESTRICTED_FG_HEX, RESTRICTED_IMAGE_STYLE } from '../restricted/constants';
import useMenu from '../../../hooks/menu/useMenu';
import useBasket from '../../../hooks/basket/useBasket';
import useTheme from '../../../hooks/ui/useTheme';
import { WithActualPrice } from '../../../providers/MenuProvider';
import { GenericProductInterface, ProductInterface } from '../../../services/exports/Interfaces';
import useStorefront from '../../../hooks/storefront/useStorefront';
import useScreenType from '../../../hooks/utility/useScreenType';
import useOffersStuff from '../../../features/offers/hooks/useOffersStuff';
import useGetMenuOfferHandlerByKind from '../../../features/offers/hooks/get-offer-handler/useGetMenuOfferHandlerByKind';
import ProductItemNutritionInfo from './ProductItemNutritionInfo';
import { useSelector } from 'react-redux';
import { selectHasCachedOrderPromoCode } from '../../../store/slices/OrderSlice';
import collect from 'collect.js';
import { OfferKindEnum } from '../../../services/exports/Constants';
import useProductIdentifiers from '../../../hooks/menu/useProductIdentifiers';
import useProductPreviewPrice from '../../../features/offers/hooks/useProductPreviewPrice';

interface Props extends PropsWithChildren {
  product: WithActualPrice<ProductInterface>;
  discount?: string | number;
  addedQuantity?: number;
  toggleProduct?: () => void;
  className?: string;
  isRestricted?: boolean;
  restrictedTooltip?: string;
}

const cardHover =
  'hover:shadow-[0_4px_6px_-1px_rgba(0,0,0,0.1),0_2px_4px_-2px_rgba(0,0,0,0.1)] transition-shadow duration-[180ms]';

const ProductItem = (props: Props): ReactElement => {
  const { product } = props;
  const { isMobile } = useScreenType();
  const { items } = useBasket();

  const addedQuantity = useMemo(
    () => items.reduce((prev, curr) => (curr.product_id === product.id ? prev + curr.quantity : prev), 0),
    [items],
  );

  const config = {
    ...props,
    addedQuantity,
  };

  return isMobile ? <ProductItemMobile {...config} /> : <ProductItemWeb {...config} />;
};

const LeftColumn = ({ product }: Props) => {
  const { isMobile } = useScreenType();
  const { decoratedName } = useProductIdentifiers(product);

  return (
    <ProductItem.LeftColumn>
      <div className={classnames('flex flex-col', isMobile ? 'space-y-[2px]' : 'space-y-[4px]')}>
        <ProductItem.BestSeller product={product} />
        <div className="flex flex-row items-center gap-sm">
          <OfferBadge product={product} />
          <ProductItemNutritionInfo.HighCaffeineWarning product={product} />
        </div>
        <div className={classnames('flex flex-col', isMobile ? 'space-y-[4px]' : 'space-y-[4px]')}>
          <ProductItem.ProductName name={decoratedName} />
          <ProductItem.PriceRow product={product} />
          {isMobile && <ProductItemNutritionInfo.PriceBreakdown product={product} />}
          <ProductItemNutritionInfo.Pfand product={product} />
          {product?.description && (
            <p
              className={classnames(
                'text-muted-foreground font-medium break-words text-xs',
                isMobile ? 'line-clamp-2' : 'line-clamp-3 mt-1.5',
              )}
            >
              {product.description}
            </p>
          )}
        </div>
      </div>
    </ProductItem.LeftColumn>
  );
};

const ProductItemMobile = (props: Props) => {
  const { product, addedQuantity, isRestricted, restrictedTooltip } = props;

  return (
    <ProductItem.Mobile {...props}>
      <LeftColumn product={product} />
      <ProductItem.RightColumn
        product={product}
        addedQuantity={addedQuantity}
        isRestricted={isRestricted}
        restrictedTooltip={restrictedTooltip}
      />
      <ProductItem.SoldOutOverlay product={product} />
    </ProductItem.Mobile>
  );
};

const ProductItemWeb = (props: Props) => {
  const { product, addedQuantity, isRestricted, restrictedTooltip } = props;

  return (
    <ProductItem.Web {...props}>
      <LeftColumn product={product} />
      <ProductItem.RightColumn
        product={product}
        addedQuantity={addedQuantity}
        isRestricted={isRestricted}
        restrictedTooltip={restrictedTooltip}
      />
      <ProductItem.SoldOutOverlay product={product} />
    </ProductItem.Web>
  );
};

type ProductNameProps = {
  name: string | ReactNode;
};

ProductItem.ProductName = (props: ProductNameProps) => {
  const { isMobile } = useScreenType();

  return (
    <div className="relative">
      <h6
        className={classnames(
          'text-brand-text-primary line-clamp-1 break-words',
          isMobile ? 'font-semibold text-[15px]' : 'font-semibold text-sm mt-0.5',
        )}
      >
        {props.name}
      </h6>
    </div>
  );
};

type GenericProps = {
  toggleProduct?: (product?: GenericProductInterface) => void;
  isRestricted?: boolean;
  product?: WithActualPrice<ProductInterface>;
} & PropsWithChildren;

// Emitted only when a real menu product is present: these two wrappers are also reused for the
// gift-card tile (GiftCardCategoryItem), which has no product and must not match the selector.
const PRODUCT_CARD_TEST_ID = 'product-card';

ProductItem.Web = (props: GenericProps): ReactElement => {
  const { toggleProduct, isRestricted, product, children } = props;
  const { productHeight } = useMenu();
  const { isStorefront } = useStorefront();

  return (
    <article
      data-test={product?.id ? PRODUCT_CARD_TEST_ID : undefined}
      data-product-id={product?.id}
      data-restricted={product?.id ? String(Boolean(isRestricted)) : undefined}
      className={classnames(
        'w-full flex flex-row select-none relative bg-brand-card overflow-hidden border border-brand-border rounded-xl p-md gap-md',
        cardHover,
        isRestricted ? 'cursor-default' : 'cursor-pointer',
        {
          '!rounded-[8px]': isStorefront,
        },
      )}
      style={{ height: productHeight, opacity: isRestricted ? 0.65 : undefined }}
      onClick={() => !isRestricted && toggleProduct && toggleProduct()}
    >
      {children}
    </article>
  );
};

ProductItem.Mobile = ({ toggleProduct, isRestricted, product, children }: GenericProps): ReactElement => {
  const { productHeight } = useMenu();

  return (
    <article
      data-test={product?.id ? PRODUCT_CARD_TEST_ID : undefined}
      data-product-id={product?.id}
      data-restricted={product?.id ? String(Boolean(isRestricted)) : undefined}
      className={classnames(
        'w-full flex flex-row items-center select-none relative overflow-hidden gap-2 py-md',
        isRestricted ? 'cursor-default' : 'cursor-pointer',
      )}
      style={{ height: productHeight, opacity: isRestricted ? 0.65 : undefined }}
      onClick={() => !isRestricted && toggleProduct && toggleProduct()}
    >
      {children}
    </article>
  );
};

ProductItem.SoldOutOverlay = ({ product }: { product: WithActualPrice<ProductInterface> }) => {
  const { t } = useTranslation(null, { keyPrefix: 'Components:Menu:MultiSelectMenuItems' });

  if (product?.is_available) {
    return null;
  }

  return (
    <div className="top-0 left-0 absolute h-full w-full z-10">
      <div className="flex flex-col items-center justify-center h-full rounded-xl bg-black bg-opacity-50">
        <div className="flex flex-col absolute inset-0 items-center justify-center">
          <p className="text-white text-center">{t('labels.sold_out')}</p>
        </div>
      </div>
    </div>
  );
};

ProductItem.PriceRow = ({ product }: { product: WithActualPrice<ProductInterface> }) => {
  const { toggleProductLike, isLiked } = useProductLikes();
  const liked = isLiked(product.id);
  const theme = useTheme();
  const { actualPrice, originalPrice } = useProductPreviewPrice(product);
  const { isMobile } = useScreenType();

  // Machine-readable mirror of the rendered prices, for e2e assertions.
  const priceMarker = (
    <span
      data-test="product-price"
      data-product-id={product?.id}
      data-actual-price={actualPrice}
      data-original-price={originalPrice}
      className="hidden"
    />
  );

  if (isMobile) {
    return (
      <div className="flex flex-row items-center w-full relative text-muted-foreground font-semibold">
        {priceMarker}
        <div className="h-full flex flex-col justify-end">
          <PriceComparison
            actualPrice={actualPrice}
            originalPrice={originalPrice}
            actualPriceStyle={{ fontWeight: 'semibold', color: theme.color.brand.text.primary, fontSize: 15 }}
            originalPriceStyle={{ fontWeight: 'semibold', fontSize: 15 }}
          />
        </div>
        <div className="flex flex-row items-center">
          <div
            className="ml-xs flex items-center"
            onClick={(e) => {
              e.stopPropagation();
              toggleProductLike(product.id);
            }}
          >
            <HeartIcon
              filled={liked}
              className={classnames('h-3', {
                'fill-danger': liked,
                'fill-gray-700': !liked,
              })}
            />
            <span className="text-xs text-muted-foreground ml-2xs w-3">{product.likes_count}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-row items-center gap-2 text-sm mt-1 w-full relative text-muted-foreground">
      {priceMarker}
      <div className="flex flex-col justify-end">
        <PriceComparison
          actualPrice={actualPrice}
          originalPrice={originalPrice}
          actualPriceStyle={{
            fontWeight: 600,
            color: theme.color.brand.text.primary,
            fontSize: 14,
          }}
          originalPriceStyle={{
            fontWeight: 500,
            fontSize: 13,
          }}
        />
      </div>
      <ProductItemNutritionInfo.PriceBreakdown product={product} />
      <button
        type="button"
        className="flex items-center gap-1"
        onClick={(e) => {
          e.stopPropagation();
          toggleProductLike(product.id);
        }}
      >
        <Heart
          className={classnames('w-3.5 h-3.5', {
            'fill-danger text-danger': liked,
            'text-muted-foreground': !liked,
          })}
        />
        <span className="text-xs text-muted-foreground">{product.likes_count}</span>
      </button>
    </div>
  );
};

ProductItem.ProductImage = ({
  name,
  image,
  addedQuantity,
  isRestricted,
  restrictedTooltip,
  children,
}: PropsWithChildren<{
  name?: string;
  image?: string;
  addedQuantity?: number;
  isRestricted?: boolean;
  restrictedTooltip?: string;
}>) => {
  const { isMobile } = useScreenType();
  const restrictedImageStyle = isRestricted ? RESTRICTED_IMAGE_STYLE : undefined;

  if (isMobile) {
    return (
      <div className="relative h-full w-[120px]">
        <div className="flex h-full shrink-none overflow-hidden relative py-md pr-sm">
          <div className="relative h-full w-full my-auto aspect-square object-cover">
            {children ?? (
              <img
                src={image}
                alt={SeoResource.toAltText(`${name} image`)}
                className="relative h-full w-full my-auto aspect-square object-cover rounded-lg"
                style={restrictedImageStyle}
              />
            )}
          </div>
        </div>
        <ProductItem.AddButton
          addedQuantity={addedQuantity}
          isRestricted={isRestricted}
          restrictedTooltip={restrictedTooltip}
        />
      </div>
    );
  }

  return (
    <div className="relative rounded-lg overflow-hidden flex-shrink-0 bg-muted w-28 h-28">
      <div className="relative w-full h-full aspect-square object-cover">
        {children ?? (
          <img
            src={image}
            alt={SeoResource.toAltText(`${name} image`)}
            className="w-full h-full object-cover"
            style={restrictedImageStyle}
          />
        )}
      </div>
      <ProductItem.AddButton
        addedQuantity={addedQuantity}
        isRestricted={isRestricted}
        restrictedTooltip={restrictedTooltip}
      />
    </div>
  );
};

ProductItem.AddButton = ({
  addedQuantity,
  className,
  withBorder,
  isRestricted,
  restrictedTooltip,
}: {
  addedQuantity: number;
  className?: string;
  withBorder?: boolean;
  isRestricted?: boolean;
  restrictedTooltip?: string;
}): ReactElement => {
  const theme = useTheme();

  const base = classnames(
    'absolute bottom-1.5 right-1.5 rounded-full w-7 h-7 flex items-center justify-center shadow-md',
    isRestricted ? `${RESTRICTED_BG_CLASS} cursor-not-allowed` : 'bg-brand-card',
  );
  const border = withBorder ? 'border border-brand-border' : undefined;
  const glyphColor = isRestricted ? RESTRICTED_FG_HEX : theme.color.brand.text.primary;

  const button = (
    <button
      type="button"
      aria-disabled={isRestricted || undefined}
      className={classnames(base, border, className)}
      onClick={isRestricted ? (e) => e.stopPropagation() : undefined}
    >
      {addedQuantity ? (
        <span className="text-xs font-semibold" style={{ lineHeight: 1, color: glyphColor }}>
          {addedQuantity}
        </span>
      ) : (
        <Plus className="h-4 w-4" style={{ color: glyphColor }} />
      )}
    </button>
  );

  return <RestrictedTooltip title={isRestricted ? restrictedTooltip : undefined}>{button}</RestrictedTooltip>;
};

ProductItem.LeftColumn = ({ children, className }: PropsWithChildren & ComponentPropsWithoutRef<'div'>) => {
  const { isMobile } = useScreenType();

  return (
    <div
      className={classnames('flex flex-col flex-1 min-w-0 relative justify-between', {
        'shrink-0': isMobile,
      })}
    >
      <div className="flex flex-row justify-between flex-1">
        <div
          className={classnames(
            'flex flex-1 flex-col justify-between min-w-0',
            isMobile ? 'gap-y-[2px]' : 'gap-y-0',
            className,
          )}
        >
          {children}
        </div>
      </div>
    </div>
  );
};

ProductItem.RightColumn = ({
  product,
  addedQuantity,
  isRestricted,
  restrictedTooltip,
}: {
  product: WithActualPrice<ProductInterface>;
  addedQuantity: number;
  isRestricted?: boolean;
  restrictedTooltip?: string;
}) => {
  const { isMobile } = useScreenType();

  if (!product?.thumbnail_url) {
    if (isMobile) {
      return (
        <div className="flex h-full w-[50px] overflow-hidden relative shrink-0">
          <ProductItem.AddButton
            addedQuantity={addedQuantity}
            isRestricted={isRestricted}
            restrictedTooltip={restrictedTooltip}
          />
        </div>
      );
    }

    return (
      <div className="h-full w-[50px] shrink-0">
        <ProductItem.AddButton
          addedQuantity={addedQuantity}
          withBorder
          isRestricted={isRestricted}
          restrictedTooltip={restrictedTooltip}
        />
      </div>
    );
  }

  return (
    <ProductItem.ProductImage
      name={product.name}
      image={product.thumbnail_url}
      addedQuantity={addedQuantity}
      isRestricted={isRestricted}
      restrictedTooltip={restrictedTooltip}
    />
  );
};

ProductItem.BestSeller = ({ product }: { product: WithActualPrice<ProductInterface> }) => {
  const { t } = useTranslation(null, { keyPrefix: 'Components:Menu:MultiSelectMenuItems' });
  const isLastOrdered = Boolean(
    (product as WithActualPrice<ProductInterface> & { order_line_item?: unknown })?.order_line_item,
  );

  if (isLastOrdered) {
    return (
      <span className="text-[10px] sm:text-xs font-semibold text-muted-foreground">{t('labels.last_ordered')}</span>
    );
  }

  if (!product.popularity_rank) {
    return null;
  }

  return (
    <span className="text-[10px] sm:text-xs font-semibold text-brand-primary">
      🏅 {t('labels.most_popular', { rank: product?.popularity_rank })}
    </span>
  );
};

interface OfferBadgeProps {
  product: WithActualPrice<ProductInterface>;
}

const OfferBadge = ({ product }: OfferBadgeProps) => {
  const { activeOffers, appliedOffers, unlockedOffers } = useOffersStuff();
  const offers = collect(activeOffers ?? [])
    .merge(appliedOffers ?? [])
    .whereNotIn('kind', [OfferKindEnum.FreeItemByMinSpendValue, OfferKindEnum.PercentageOffByMinSpendValue])
    .merge(unlockedOffers ?? [])
    .unique('id')
    .toArray();
  const hasPromoCode = useSelector(selectHasCachedOrderPromoCode);
  const getMenuOfferHandlerByKind = useGetMenuOfferHandlerByKind();

  if (hasPromoCode || !offers?.length) {
    return null;
  }

  const handlers = offers
    .map(getMenuOfferHandlerByKind)
    .filter((handler) => handler?.isEligible && handler?.isEligible(product));

  if (!handlers.length) {
    return null;
  }

  return (
    <>
      {handlers.map((handler) => (
        <p
          className="w-fit bg-brand-primary/30 text-xs p-1 rounded flex items-center justify-center text-brand-primary font-medium"
          key={handler.badge}
        >
          {handler.badge}
        </p>
      ))}
    </>
  );
};

export default ProductItem;
